'use strict'

class Mapping{

  static mapLocation(loc, source){
    // loc may be null and we don't want that. Default arg value is only for undefined, not null, so this is needed
    if (loc == null)
      loc = {}
    return {
      address: loc.Address || '',
      city: loc.City || '',
      zipCode: loc.ZipCode  || '',
      region: loc.State || '',
      country: loc.Country || '',
      latitude: loc.Latitude || 0.0,
      longitude: loc.Longitude || 0.0,
      source: source || loc.Source || '',
      auto: !!loc.auto,
      type: loc.PlaceType || '',
      isDefault: !!loc.IsDefaultLocation
    }
  }

  static mapProducts(pdts = []){
    return pdts.map(pdt => {
      return {
        id: pdt.pid || '',
        name: pdt.pname || '',
        name2: pdt.pname2 || '',
        imgUrl: pdt.pimgurl || '',
        description: pdt.pdescr || '',
        longDescription: pdt.plongdescr || '',
        ean: pdt.ean || '',
        eans: pdt.eans || [],
        default: pdt.default || false,
        brand: pdt.pbr || '',
        brandImgUrl:  pdt.pbrimgurl || '',
        pkg: pdt.ppckg || '',
        distribution: pdt.dn || 0,
        retailers: pdt.rets || [],
        thumbImgUrl: pdt.ptbimgurl || '',
        moreContentUrl: pdt.cturl || '',
        moreContentImgUrl: pdt.ctimgurl || ',',
        indb: pdt.indb,
        tags: pdt.tags || [],
        ratings: {
          reviewCount: pdt.reviewct,
          average: pdt.avgrating,
          roundedAverage: pdt.rdavgrating
        },
        model: pdt.model || '',
        catName: pdt.catname || '',
        sourcePid: pdt.srcpid || '',
        range: pdt.range || '',
        productLine: pdt.productLine || '',
        mpn: pdt.mpn || '',
      }
    })
  }

  // Replaces {SWN-<PROPNAME>} macros in url with the value for PROPNAME
  static _replaceMacros(url, replacements = {}){
    const props = ['PAGEID', 'IID', 'SID', 'SURL', 'RFR2', 'SRFR', 'RFR']
    for (let i = 0; i < props.length; i++){
      if (replacements[props[i]])
        url = url.replace(`{SWN-${props[i]}}`, encodeURIComponent(replacements[props[i]]))
    }
    return url
  }

  static mapAvailabilities(avails = [], replacements){
    return avails.map(x => {
      const leadid = Utils.uuid().replace(/-/g, ''),
            rawUrl = x.clkurl ? Mapping._replaceMacros(x.clkurl, replacements) : ''
      return {
        product: {
          id: x.pid || '',
          ean: x.ean || '',
          name: x.pname || '',
          img: x.pimgurl || '',
          thumbImgUrl: x.ptbimgurl || '',
          pkg: x.ppckg || '',
          containing: x.pcont || '',
          retailerPdtId: x.rpid || '',
          url: x.purl || '',
          available: x.av || false,
        },

        retailer: {
          id: x.rid || 0,
          code: x.rcode || '',
          name: x.rname || '',
          url: x.rurl || '',
          logo: x.rlogo || `https://content-nocache.static-swaven.com/img/retailers/hd/${x.rlogoid}.png`, // default logo url
          logoId: x.rlogoid || '',
          isDefaultLogo: x.rlogodefault || false,
          affiliate: x.raff || false,
          affiliatePrgId: x.raffprgid || null,
          isTiedHouseLaw: x.isTiedHouseLaw || false,
          category: x.rcat || '',
        },

        store: {
          id: x.sid || '',
          name: x.sname || '',
          location: {
            address: x.saddr || '',
            zipCode: x.szc || '',
            city: x.scity || '',
            state: x.sstate || '',
            country: x.scountry || '',
            latitude: x.slat || 0.0,
            longitude: x.slng || 0.0,
          },
          amz: x.amz || false,
          typologyId: x.stid || 0,
          openingType: x.sopeningt || 0,
          opening: x.sopening || '',
          openingStruct: x.sopeningstruct || null,
          homeDelivery: x.shd || false,
          homeDeliveryAll: x.shda || false,
          collect: x.scollect || false,
          drive: x.sdrive || false,
          brick: x.sbrick || false,
          url: x.surl || '',
          online: !!x.sonline,
          offline: !!x.soffline,
          gmapUrl: x.sgmapurl || '',
          directionsUrl: x.sdirectionsurl || '',
          distance: x.sdst || 0.0,
          phone: x.sphonenumber || '',
          phoneIntl: x.sphoneintl || '',
          whatsapp: x.swhatsapp || '',
          tags: x.stags || null ,
          imgUrl: x.simgurl || '',
          openingHours: Mapping._parseOpeningHours(x.sopeningschema),
          openingType: x.sopeningt || null,
          open24: x.sopen24 || false,
          timezone: x.stz || '',
          appointmentUrl: x.sappointmenturl || '',
          attributes: x.scustomattributes || {},
        },
        nearestStore: null,

        price: {
          canShow: x.prok || false,
          amount: x.pr || 0.0,
          unitPrice: x.upr || 0.0,
          unitPriceLbl: x.uprlbl || '',
          refPrice: x.refpr || 0.0,
          promo: x.pmo || false,
          promoType: x.pmotype || 0,
          promoLbl: x.pmolbl || '',
          promoPct: x.pmopct || 0.0,
          promoAmount: x.pmoam || 0.0,
          currency: x.currency,
        },

        priority: x.priority || false,
        cartUrl: x.carturl || '',
        rawClkUrl: rawUrl, // redirect url with constant-value placeholders replaced
        clkUrl: rawUrl ? rawUrl.replace('{SWN-LEADID}', leadid) : '', // redirect url with leadid placeholder replaced by actual value
        clkUrlType: x.clkurlt || 0,
        clkUrlAffiliate: x.clkurlaff || false,
        leadid: leadid,
        lockdown: !!x.slockdown
      }
    })
  }

  // product filters on a property are defined using the base model, not the more explicit one defined in this widget.
  // so let's map possible values to what's actually used.
  static mapPdtFilters(values){
    if (values == null) return []

    return values.map(x => {
      let value
      switch (x){
        case 'pbr': value = 'brand'; break
        case 'pname': value = 'name'; break
        case 'ppckg': value = 'pkg'; break
        case 'pdescr': value = 'description'; break
        default: value = x
      }
      return value
    })
  }

  // opening hours values are split to show separately days and hours
  static _parseOpeningHours(values){
    if (!values || values.length === 0) return null

    return values.map(line => line.split(' '))
  }
}
